/**
 * App shell (FOUNDATION — BUILD-PLAN src/app.tsx).
 * Wires: session bootstrap (refresh-cookie → authed/anon), theme application,
 * online/offline tracking, the PWA install prompt capture, the global toast
 * and announcer hosts, and the router. Feature content lives in routes.
 */
import { useEffect } from "react";
import { MotionConfig } from "framer-motion";
import { AnnouncerHost, ToastHost } from "./design/index.js";
import { bootstrapSession } from "./lib/api.js";
import {
  applyTheme,
  sessionStore,
  uiStore,
  useStore,
  type BeforeInstallPromptEvent,
} from "./lib/store.js";
import { ws } from "./lib/ws.js";
import { AppRouter } from "./router.js";

export function App() {
  const status = useStore(sessionStore, (s) => s.status);

  // one-time bootstrap
  useEffect(() => {
    applyTheme(uiStore.get().theme);
    void bootstrapSession();

    const onOnline = () => uiStore.set({ online: true });
    const onOffline = () => uiStore.set({ online: false });
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    const onInstallPrompt = (e: Event) => {
      e.preventDefault();
      uiStore.set({ installPrompt: e as BeforeInstallPromptEvent });
    };
    window.addEventListener("beforeinstallprompt", onInstallPrompt);
    const onInstalled = () => uiStore.set({ installPrompt: null });
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeinstallprompt", onInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // the realtime plane lives and dies with the session
  useEffect(() => {
    if (status === "authed") ws.ensureConnected();
    else if (status === "anon") ws.disconnect();
  }, [status]);

  return (
    // reducedMotion="user": every framer transform/layout animation collapses
    // automatically for prefers-reduced-motion users (PRD §10)
    <MotionConfig reducedMotion="user">
      <AppRouter />
      <ToastHost />
      <AnnouncerHost />
    </MotionConfig>
  );
}
